import { useState, useRef, useEffect, useCallback, memo } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { PlayIcon, PauseIcon } from "@hugeicons-pro/core-solid-rounded";
import { Tick02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import {
  PLAYBACK_RATES,
  formatPlaybackRate,
  setPlaybackRate,
  usePlaybackRate,
  type PlaybackRate
} from "shared/hooks/use-playback-rate";
import type { CallPlayback } from "shared/hooks/use-call-playback";

const WAVEFORM_BARS = [
  15, 25, 20, 45, 30, 55, 25, 40, 60, 20, 35, 50, 25, 65, 30, 20, 40, 55, 25,
  70, 35, 20, 45, 30, 55, 20, 40, 65, 25, 50, 35, 20, 75, 30, 45, 25, 55, 20,
  40, 60, 30, 25, 50, 35, 20, 45, 70, 25, 55, 30, 40, 20, 60, 35, 25, 45, 20,
  50, 65, 30, 25, 40, 55, 20, 35, 75, 25, 45, 30, 60, 20, 50, 35, 25, 40, 55,
  20, 65, 30, 45, 25, 50, 20, 35, 60, 25, 70, 40, 20, 55, 30, 45, 25, 20, 50,
  35, 65, 25, 40, 20, 55, 30, 45, 75, 20, 35, 50, 25, 60, 20, 40, 30, 55, 25,
  45, 20, 65, 35, 50, 25, 30, 40, 20, 55, 70, 25, 40, 55, 20, 35, 60, 25, 45,
  30, 70, 20, 50, 35, 25, 55, 40, 65, 20, 30, 45, 25, 50, 75, 20, 40, 35, 55,
  25, 60, 30, 20, 45, 50, 25, 65, 35, 40, 20, 55, 70, 30, 25, 45, 20, 50, 35,
  60, 25, 40, 75, 20, 55, 30, 45, 25, 35, 50, 20, 65, 40, 25, 55, 30, 45, 20,
  60, 35, 70, 25, 50, 40, 20, 55, 25, 45, 30, 65, 20, 35, 50, 40, 25, 55, 75,
  20, 30, 45, 60, 25, 35, 50, 40, 20, 55, 25, 70, 30, 45, 35, 20, 60, 50, 25,
  40, 55, 65, 20, 35, 30, 45, 25, 50, 20, 55, 40, 30, 65, 25, 35, 70, 45, 20,
  50, 60, 25, 40, 55
];

const formatTime = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0)
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;

  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// Progress is written straight to the DOM instead of through React state: the
// rAF loop runs at frame rate and re-rendering the card that often would be
// wasteful. Kept module-level and stateless so a seek can repaint on demand.
const paintProgress = (
  waveform: HTMLDivElement | null,
  timeEl: HTMLSpanElement | null,
  currentTime: number,
  duration: number
) => {
  if (waveform) waveform.style.width = `${(currentTime / duration) * 100}%`;
  if (timeEl)
    timeEl.textContent = `${formatTime(currentTime)}/${formatTime(duration)}`;
};

let activeAudioPlayer: { pause: () => void; reset: () => void } | null = null;

// The speed applies to every recording in the tab, so the menu writes to the
// shared store rather than to this player (CRM-1330).
const PlaybackRateControl = ({ disabled }: { disabled: boolean }) => {
  const rate = usePlaybackRate();
  const [open, setOpen] = useState(false);

  const select = (next: PlaybackRate) => {
    setPlaybackRate(next);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement="bottom-start"
      dismissOnScroll
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label="Playback speed"
          onClick={() => setOpen((v) => !v)}
          className="flex h-7 shrink-0 items-center justify-center rounded-full border border-alpha-100 px-2 text-xs font-medium text-content-400 hover:bg-alpha-20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {formatPlaybackRate(rate)}
        </button>
      </PopoverTrigger>
      {/* Above react-modern-drawer, which hosts one of these players. */}
      <PopoverContent className="z-[500]">
        <div className="flex w-[104px] flex-col rounded-lg border border-alpha-100 bg-white py-1 shadow-xl">
          {PLAYBACK_RATES.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => select(option)}
              className={cx(
                "flex items-center justify-between px-3 py-1.5 text-sm font-medium hover:bg-alpha-20 transition-colors",
                option === rate ? "text-primary-500" : "text-content-1000"
              )}
            >
              {formatPlaybackRate(option)}
              {option === rate && (
                <HugeiconsIcon icon={Tick02Icon} size={14} strokeWidth={3} />
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const WaveformBar = ({ h, className }: { h: number; className: string }) => (
  <div
    className={cx("w-px shrink-0 rounded-full", className)}
    style={{ height: `${h}%` }}
  />
);

const WaveformBars = memo(function WaveformBars({
  waveformRef
}: {
  waveformRef: React.RefObject<HTMLDivElement>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    // eslint-disable-next-line compat/compat -- ResizeObserver is supported in all target browsers
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="relative flex-1 h-9 overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-between gap-[1.5px]">
        {WAVEFORM_BARS.map((h, i) => (
          <WaveformBar key={i} h={h} className="bg-alpha-100" />
        ))}
      </div>
      <div
        ref={waveformRef}
        className="absolute inset-y-0 left-0 overflow-hidden"
        style={{ width: "0%" }}
      >
        <div
          className="flex h-full items-center justify-between gap-[1.5px]"
          style={{ width: containerWidth || "100%" }}
        >
          {WAVEFORM_BARS.map((h, i) => (
            <WaveformBar key={i} h={h} className="bg-primary-500" />
          ))}
        </div>
      </div>
    </div>
  );
});

interface CallRecordingPlayerProps {
  recordingUrl: string;
  durationSeconds?: number | null;
  disabled?: boolean;
  // When false, playback is stopped and rewound. Lets a parent that keeps this
  // mounted (e.g. a drawer that only toggles `open`) halt audio on close.
  active?: boolean;
  // Bus shared with the transcript rendered beside this player (CRM-1330).
  // Absent for a player with no transcript next to it.
  playback?: CallPlayback;
}

export const CallRecordingPlayer = ({
  recordingUrl,
  durationSeconds,
  disabled = false,
  active = true,
  playback
}: CallRecordingPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);
  // Authoritative duration from the loaded audio metadata; falls back to the
  // prop until it loads. Derived (not synced via effect) to avoid prop/state drift.
  const [audioDuration, setAudioDuration] = useState<number | null>(null);
  const duration = Math.max(audioDuration ?? durationSeconds ?? 0, 1);
  const audioRef = useRef<HTMLAudioElement>(null);
  const rafRef = useRef<number>(0);
  const waveformRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);
  const isPlayingRef = useRef(false);
  const rate = usePlaybackRate();

  // Back to the start: stop the loop, blank the bar, and tell subscribers there
  // is no current position (so a synced transcript drops its highlight).
  const rewind = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    if (waveformRef.current) waveformRef.current.style.width = "0%";
    if (timeRef.current)
      timeRef.current.textContent = `0:00/${formatTime(duration)}`;
    playback?.publish(null);
  }, [duration, playback]);

  const pause = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    isPlayingRef.current = false;
    setIsPlaying(false);
    activeAudioPlayer = null;
  }, []);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (activeAudioPlayer) activeAudioPlayer.pause();

    audio
      .play()
      .then(() => {
        isPlayingRef.current = true;
        setIsPlaying(true);
        activeAudioPlayer = {
          pause: () => {
            audioRef.current?.pause();
            isPlayingRef.current = false;
            setIsPlaying(false);
          },
          reset: rewind
        };
      })
      .catch(() => {
        isPlayingRef.current = false;
        setIsPlaying(false);
      });
  }, [rewind]);

  const seekTo = useCallback(
    (seconds: number) => {
      const audio = audioRef.current;
      if (!audio) return;

      const next = Math.min(Math.max(seconds, 0), duration);
      audio.currentTime = next;
      // The rAF loop paints only while playing, so a seek made while paused has
      // to repaint and republish itself — otherwise the bar and the synced
      // transcript stay where they were.
      paintProgress(waveformRef.current, timeRef.current, next, duration);
      playback?.publish(next);
    },
    [duration, playback]
  );

  useEffect(() => {
    const tick = () => {
      // Scheduled first so every path below can return early. rafRef always
      // holds the pending handle, so the cleanup cancels the right frame.
      // eslint-disable-next-line compat/compat -- requestAnimationFrame is supported in all target browsers
      rafRef.current = requestAnimationFrame(tick);

      const audio = audioRef.current;
      if (!audio || !isPlayingRef.current) return;

      if (audio.ended) {
        rewind();
        return;
      }

      const d = Math.max(audio.duration || duration, 1);
      paintProgress(waveformRef.current, timeRef.current, audio.currentTime, d);
      playback?.publish(audio.currentTime);
    };

    // eslint-disable-next-line compat/compat -- requestAnimationFrame is supported in all target browsers
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [duration, playback, rewind]);

  // Clear stale error state whenever the source changes. The drawer mounts this
  // player with an empty URL while the details query loads, which fires a spurious
  // `error` event; without this reset it would stick even after the real URL arrives.
  useEffect(() => {
    setAudioError(false);
  }, [recordingUrl]);

  // Speed is a tab-wide preference, so it is (re)applied here rather than only
  // when the user picks it. defaultPlaybackRate is set too: loading a new source
  // resets playbackRate to it, which is how a speed chosen before the presigned
  // URL arrived survives into playback.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.defaultPlaybackRate = rate;
    audio.playbackRate = rate;
  }, [rate, recordingUrl]);

  // The handlers are rebuilt whenever `duration` changes (metadata loading, a
  // refetched recording length). Registration must NOT churn with them: its
  // cleanup clears the bus position, which would blank the transcript's
  // highlight mid-playback. So register once per bus and read the current
  // handlers through a ref.
  const handlersRef = useRef({ play, pause, seek: seekTo });

  useEffect(() => {
    handlersRef.current = { play, pause, seek: seekTo };
  }, [play, pause, seekTo]);

  // Hands the transcript a way to drive this player, and takes it back on
  // unmount so a stale card can't seek a player that is no longer on screen.
  useEffect(() => {
    if (!playback) return;

    playback.register({
      play: () => handlersRef.current.play(),
      pause: () => handlersRef.current.pause(),
      seek: (seconds) => handlersRef.current.seek(seconds)
    });
    return () => playback.register(null);
  }, [playback]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoaded = () => {
      if (audio.duration !== Infinity) setAudioDuration(audio.duration);
    };
    const onError = () => {
      setAudioError(true);
      setIsPlaying(false);
    };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("ended", rewind);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("ended", rewind);
      audio.removeEventListener("error", onError);
    };
  }, [recordingUrl, rewind]);

  // Stop and rewind when the player is deactivated (e.g. the drawer closes) so
  // audio doesn't keep playing in the background.
  useEffect(() => {
    if (active) return;

    audioRef.current?.pause();
    rewind();
  }, [active, rewind]);

  const togglePlayback = () => {
    if (isPlaying) pause();
    else play();
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const timeWidth = 60;
    const barWidth = rect.width - timeWidth;
    if (barWidth <= 0) return;

    const x = Math.max(0, Math.min(e.clientX - rect.left, barWidth));
    seekTo((x / barWidth) * duration);
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={togglePlayback}
          disabled={disabled || audioError}
          className="flex size-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <HugeiconsIcon icon={isPlaying ? PauseIcon : PlayIcon} size={16} />
        </button>
        <PlaybackRateControl disabled={disabled || audioError} />
        <div
          className="flex flex-1 items-center gap-2 cursor-pointer"
          onClick={handleSeek}
        >
          <WaveformBars waveformRef={waveformRef} />
          <span
            ref={timeRef}
            className="flex-shrink-0 text-xs w-[60px] text-right font-medium"
          >
            0:00/{formatTime(duration)}
          </span>
        </div>
      </div>
      <audio
        ref={audioRef}
        src={recordingUrl || undefined}
        preload="metadata"
      />
      {audioError && (
        <p className="mt-1 text-xs text-danger-500">Recording unavailable</p>
      )}
    </div>
  );
};
